'use client';

import classnames from 'classnames';
import React, { FC } from 'react';

type As<Props = any> = React.ElementType<Props>;

interface IButtonProps extends Record<string, unknown> {
	children?: React.ReactNode;
	as?: As;
	className?: string;
	icon?: React.ReactNode;
	variant?: 'primary' | 'outline' | 'ghost' | 'faint';
	size?: 'sm' | 'md' | 'lg';
}

const baseClasses =
	'inline-flex items-center justify-center font-medium transition-colors rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:pointer-events-none disabled:opacity-50';

const sizeClasses: Record<NonNullable<IButtonProps['size']>, string> = {
	sm: 'h-8 px-3 text-xs',
	md: 'h-9 px-4 text-base',
	lg: 'h-11 px-6 text-lg',
};

const variantClasses: Record<NonNullable<IButtonProps['variant']>, string> = {
	primary: 'bg-red-500 text-white hover:bg-red-700',
	outline: 'border border-grey-600 hover:bg-gray-200',
	ghost: 'hover:bg-gray-200',
	faint: 'bg-gray-200 hover:bg-gray-300',
};

export const Button: FC<IButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
	children,
	as: As = 'button',
	className,
	variant = 'primary',
	size = 'md',
	icon,
	...buttonProps
}) => {
	const computedClassName = classnames(baseClasses, sizeClasses[size], variantClasses[variant], className);

	return (
		<As className={computedClassName} {...buttonProps}>
			{icon && <span className="mr-2 inline-flex items-center">{icon}</span>}
			{children}
		</As>
	);
};
